import React, { useState, useEffect } from 'react';
import {
  downloadProduct,
  downloadPflProduct
} from '../../helpers/pages/collections/collectionsHelpers';
import { DownloadIcon } from '../../modules/Icons';
import { prettyFileSize } from '../../helpers/pages/collections/collectionsHelpers';
import { connect, useDispatch } from 'react-redux';
import { addMessage } from '../../state/actions/messagesActions';
import BasicModal from '../shared/BasicModal';
import { MainLinkButton } from '../styled/buttons/MainLinkButton';
import { AUTH_ACTIONS, DOWNLOAD_STATUS_ACTIONS } from '../../constants/actions';
import { QUERY_PARAMS as ALGOLIA_QUERY_PARAMS } from '../../constants/algolia';
const { AUTH_SHOW_LOGIN } = AUTH_ACTIONS;
import EmailNotVerifiedDialog from '../shared/EmailNotVerifiedDialog';
import { productVariantCanBeDownloaded } from '../../helpers/contentCanBeDownloaded';
import { ApplicationState } from '../../types/state/storeTypes';
import { productIsPFL } from '../../helpers/productHelpers';
import { CollectionVariantDownload } from '../../types/api/DownloadsTypes';
import { GenericResponse } from '../../types/api/Http';
import { ProductVariantDownloadResponse, ProductDownloadResponse } from '../../types/api/DownloadsTypes';
import { ApiError } from '../../types/api/ErrorsTypes';
import downloadsHelpers from '../../helpers/downloadsHelpers';
const { downloadsSortingFunction, downloadResponseHandlerFunction } = downloadsHelpers;
import { useRouter } from 'next/router';

const DownloadList: React.FC<any> = (props) => {
  const {
    downloads,
    user,
    product,
    setSelectedProduct = () => {},
    onDownloadStarted = () => {},
    userSubscription,
    collectionId,
  } = props;

  const [downloaded, setDownloaded] = useState({});
  const [loginModalVisible, setLoginModalVisible] = useState<boolean>(false);
  const [emailNotVerifiedModalVisible, setEmailNotVerifiedModalVisible] = useState<boolean>(false);

  const dispatch = useDispatch();
  const router = useRouter();

  useEffect(() => {
    if (!user?.token) {
      setLoginModalVisible(true);
    }
  }, [user]);

  const orderedDownloads = (): Array<CollectionVariantDownload> => {
    const _downloads = downloads || product?.variant_downloads || [];
    return _downloads.sort(downloadsSortingFunction);
  };

  const handleDownloadResponse = (
    response: GenericResponse<
      ProductVariantDownloadResponse | ProductDownloadResponse | ApiError | string | null
    >
  ) => {
    const actions = downloadResponseHandlerFunction(response);
    actions.forEach((action) => dispatch(action));
  };

  if (!user?.token) {
    return (
      <BasicModal
        visible={loginModalVisible}
        setVisible={setLoginModalVisible}
        content={'login-redirect'}>
        <div>
          <h3 className="uppercase eurostile text-[24px] tracking-wide font-semibold">
            You must be logged in to access Practice Footage Library downloads
          </h3>

          <p className="text-[18px] py-4 mb-2">
            Please{' '}
            <a
              onClick={() => {
                setLoginModalVisible(false);
                if (setSelectedProduct) {
                  setSelectedProduct(null);
                }
                dispatch({ type: AUTH_SHOW_LOGIN });
              }}
              href="#"
              className="text-a-blue">
              login
            </a>{' '}
            or{' '}
            <a
              href="/pricing"
              className="text-a-blue">
              sign up for a free account
            </a>{' '}
            before proceeding.
          </p>

          <div className="mt-4 text-center">
            <MainLinkButton
              inline={true}
              variant="default"
              onClick={() => setLoginModalVisible(false)}>
              Close Window
            </MainLinkButton>
          </div>
        </div>
      </BasicModal>
    );
  }

  function renderDownloadsDropdown(variantPrices: Array<any>) {
    return (
      <ul
        className={loginModalVisible ? 'hidden' : 'flex flex-col gap-2 xl:gap-0'}
        cy-test-id="download-list">
        {orderedDownloads().map((d: CollectionVariantDownload) => (
          <li
            key={d.id}
            cy-test-id={`download-${d.id}`}
            className="xl:pt-[8px] xl:pb-[11px] xl:px-5">
            <button
              className="flex items-center gap-3 text-[#AEB6BA] text-[16px] stroke-[#AEB6BA] hover:text-a-blue hover:stroke-a-blue cursor-pointer"
              onClick={async (e) => {
                e.stopPropagation();
                if ((productIsPFL(product) || product.free_product) && d.resolution.includes('4K') && !userSubscription?.id) {
                  dispatch({ type: DOWNLOAD_STATUS_ACTIONS.SHOW_DOWNLOAD_LIMIT_4K });
                  return false;
                } else if (downloaded[d.id]) {
                  return false;
                }

                if (!productVariantCanBeDownloaded(user, d.variant_id, variantPrices, product)) {
                  setEmailNotVerifiedModalVisible(true);
                  return false;
                }

                setDownloaded({ ...downloaded, ...{ [d.id]: true } });

                let status = null;
                if (product?.variant_downloads?.length > 0) {
                  status = await downloadPflProduct(d.id, user, handleDownloadResponse, collectionId, router.query[ALGOLIA_QUERY_PARAMS.QUERY_ID] as string);
                  onDownloadStarted();
                  return false;
                } else {
                  status = await downloadProduct(d.id, user, handleDownloadResponse, collectionId, router.query[ALGOLIA_QUERY_PARAMS.QUERY_ID] as string);
                  onDownloadStarted();
                }
                if (!status) {
                  setDownloaded({ ...downloaded, ...{ [d.id]: false } });
                  dispatch(
                    addMessage({
                      type: 'error',
                      body: "You've already attempted to download this product recently. Please wait 10 minutes or contact our support team for assistance."
                    })
                  );
                }
              }}>
              <DownloadIcon className="inline" />
              <span className="text-left text-[13px] xl:text-[16px]">
                {d?.display_override?.length > 0
                  ? d.display_override
                  : d.resolution + ' ' + d.format}
                {downloaded[d.id] && (
                  <span className="text-[#4D5457] text-[13px] text-left">
                    <br />
                    (downloading)
                  </span>
                )}
              </span>
              <span className="text-[#4D5457] text-[13px] xl:text-[16px]">
                {prettyFileSize(d.size)}
              </span>
              {d.resolution == '4K' && (productIsPFL(product) || product.free_product) && (
                <span className="float-right strong uppercase text-[12px] font-bold bg-[#4D5457] text-white px-2 py-[2px] rounded-[3px]">
                  PRO
                </span>
              )}
            </button>
          </li>
        ))}
      </ul>
    );
  }

  function renderModals() {
    return (
      <>
        <EmailNotVerifiedDialog
          isOpen={emailNotVerifiedModalVisible}
          setIsOpen={setEmailNotVerifiedModalVisible}
        />
      </>
    );
  }

  // render PFL and return early before loading variant prices
  if (productIsPFL(product) || product.free_download) {
    return (
      <div cy-test-id="download-list">
        {renderDownloadsDropdown([])}
        {renderModals()}
      </div>
    );
  }

  // render paid content
  return (
    <div cy-test-id="download-list">
      {renderDownloadsDropdown([])}
      {renderModals()}
    </div>
  );
};

const mapDispatchToProps = (state: ApplicationState) => ({
  productVariantsPrices: state.productVariantsPrices.prices
});

export default connect(mapDispatchToProps)(DownloadList);
